import { Component, OnInit } from '@angular/core';
import { NzTableFilterFn, NzTableSortFn } from 'ng-zorro-antd/table';
import {
  ExportStatus,
  FileMetadata,
  ListFileMetadataQuery,
  ListFileMetadataQueryVariables,
  UploadStatus,
} from '../../API';
import { ListFileMetadata } from '../../graphql/queries';
import { ApiService } from '../shared/services/api.service';
import { UserManagementService } from '../shared/services/user-management.service';
import { getEndOfDay, getStartOfDay, matchesDateRange } from '../shared/utils/date.util';
import { createTableFilterFn, createTableSortFn } from '../shared/utils/table.util';
import { orgMap } from '../shared/models/org';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ValidationLogsComponent } from '../file-upload/components/validation-logs/validation-logs.component';
import { NzMessageService } from 'ng-zorro-antd/message';

type StatusFilter = UploadStatus.COMPLETED | UploadStatus.FAILED;

const uploadStatusLabels: Record<UploadStatus, string> = {
  [UploadStatus.COMPLETED]: 'Completed',
  [UploadStatus.FAILED]: 'Failed',
  [UploadStatus.PROCESSING]: 'Processing',
  [UploadStatus.UPLOADED]: 'Uploaded',
  [UploadStatus.VALIDATION_FAILED]: 'Validation Failed',
  [UploadStatus.VALIDATION_SUCCESS]: 'Validation Success',
};

const exportStatusLabels: Record<ExportStatus, string> = {
  [ExportStatus.COMPLETED]: 'Completed',
  [ExportStatus.FAILED]: 'Failed',
  [ExportStatus.IN_PROGRESS]: 'In Progress',
};

@Component({
  selector: 'app-upload-history',
  templateUrl: './upload-history.component.html',
  styleUrl: './upload-history.component.scss',
})
export class UploadHistoryComponent implements OnInit {
  readonly uploadStatus = UploadStatus;

  selectedStatus: StatusFilter | null = null;
  selectedDateRange: Date[] | null = null;
  readonly dateRangePlaceholders = ['Start Date', 'End Date'];
  isLoading = false;
  readonly sortByFileId: NzTableSortFn<FileMetadata> = createTableSortFn((upload) => upload.fileId);
  readonly sortByOrgName: NzTableSortFn<FileMetadata> = createTableSortFn(
    (upload) => this.getOrgNameValue(upload.orgId) ?? upload.orgId,
  );
  readonly sortByRecordCount: NzTableSortFn<FileMetadata> = createTableSortFn((upload) => upload.recordCount ?? 0);
  readonly sortByUploadedAt: NzTableSortFn<FileMetadata> = createTableSortFn((upload) =>
    this.getUploadedAtValue(upload.uploadedAt),
  );
  readonly sortByUploadBy: NzTableSortFn<FileMetadata> = createTableSortFn((upload) => this.getUploadedBy(upload));
  readonly sortByProcessStatus: NzTableSortFn<FileMetadata> = createTableSortFn((upload) =>
    this.getUploadStatusLabel(upload.uploadStatus),
  );
  readonly sortByPublishStatus: NzTableSortFn<FileMetadata> = createTableSortFn((upload) =>
    this.getExportStatusLabel(upload.exportStatus),
  );
  readonly sortByStatusMessage: NzTableSortFn<FileMetadata> = createTableSortFn((upload) =>
    this.getStatusMessage(upload),
  );
  readonly sortByOperatorsProcessed: NzTableSortFn<FileMetadata> = createTableSortFn((upload) =>
    (upload.operatorsProcessed ?? []).join(', '),
  );

  readonly filterByOrgName: NzTableFilterFn<FileMetadata> = createTableFilterFn<FileMetadata>((item) => {
    return this.getOrgNameValue(item.orgId) || item.orgId;
  });

  uploadHistory: FileMetadata[] = [];
  filterList: { text: string; value: string }[] = [];
  isNflAdmin: boolean = false;

  statusValue: string | null = null;

  searchText = '';

  startDate: string | null = null;
  endDate: string | null = null;

  tableFilterValue: string | null = null;

  sortField: string | null = null;
  sortOrder: string | null = null;

  pageSize = 10;
  pageIndex = 1;

  cursorHistory: (string | null)[] = [];

  nextToken: string | null | undefined = null;
  hasNextPage = false;

  get filteredUploadHistory(): FileMetadata[] {
    const search = this.searchText.trim().toLowerCase();
    const [startDate, endDate] = this.selectedDateRange ?? [];
    const startTime = startDate ? getStartOfDay(startDate).getTime() : null;
    const endTime = endDate ? getEndOfDay(endDate).getTime() : null;

    return this.uploadHistory.filter((upload) => {
      const matchesSearch = !search || this.getSearchText(upload).includes(search);
      const matchesStatus =
        !this.selectedStatus || this.matchesSelectedStatus(upload.uploadStatus, upload.exportStatus);
      const matchesDate = matchesDateRange(this.getUploadedAtValue(upload.uploadedAt), startTime, endTime);

      return matchesSearch && matchesStatus && matchesDate;
    });
  }

  constructor(
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    private modalService: NzModalService,
    private nzMessageService: NzMessageService,
  ) {
    this.isNflAdmin = this.userManagementService.isNflAdmin();
  }

  ngOnInit(): void {
    this.loadUploadHistory();
  }

  async loadUploadHistory(): Promise<void> {
    this.isLoading = true;
    const requestPayload = this.createRequestPayload();

    try {
      const response = await this.apiService.query<ListFileMetadataQuery, ListFileMetadataQueryVariables>(
        ListFileMetadata,
        requestPayload,
      );

      this.uploadHistory = response.data?.ListFileMetadata.items ?? [];
      this.nextToken = response.data?.ListFileMetadata.nextToken;
      this.hasNextPage = !!response.data?.ListFileMetadata.nextToken;

      this.getOrgFilterList();
    } catch (error) {
      console.warn(error);
      this.uploadHistory = [];
    } finally {
      this.isLoading = false;
    }
  }

  createRequestPayload(): ListFileMetadataQueryVariables {
    const [startDate, endDate] = this.selectedDateRange ?? [];
    const startTime = startDate ? getStartOfDay(startDate).toISOString() : null;
    const endTime = endDate ? getEndOfDay(endDate).toISOString() : null;

    const cursor = this.cursorHistory[this.pageIndex - 1];

    const requestBody: ListFileMetadataQueryVariables = {
      input: {
        nextToken: cursor,
        orgId: this.userManagementService.getOrgId(),
        startDate: startTime ? String(startTime) : null,
        endDate: endDate ? String(endTime) : null,
        statusValue: this.selectedStatus,
        tableFilter: this.tableFilterValue,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
      },
    };

    return requestBody;
  }

  onStatusChange(value: string | null): void {
    this.statusValue = value;
    this.pageIndex = 1;
    this.nextToken = null;

    this.loadUploadHistory();
  }

  onDateRangeChange(value: Date[] | null): void {
    this.pageIndex = 1;
    this.nextToken = null;

    this.loadUploadHistory();
  }

  nextPage(): void {
    if (!this.nextToken || this.isLoading) return;

    this.cursorHistory[this.pageIndex] = this.nextToken;
    this.pageIndex++;

    this.loadUploadHistory();
  }

  previousPage(): void {
    if (this.pageIndex <= 1 || this.isLoading) return;

    this.pageIndex--;
    this.loadUploadHistory();
  }

  getStatusMessage(metadata: FileMetadata): string {
    if (metadata.uploadStatus === UploadStatus.FAILED || metadata.uploadStatus === UploadStatus.VALIDATION_FAILED) {
      return metadata.errors?.errorDetails?.join(', ') || 'Upload failed. Please try again.';
    }

    if (metadata.exportStatus === ExportStatus.FAILED) {
      return metadata.errors?.errorDetails?.join(', ') || 'Publish failed. Please try again.';
    }

    if (metadata.uploadStatus === UploadStatus.PROCESSING || metadata.exportStatus === ExportStatus.IN_PROGRESS) {
      return 'File processing in progress';
    }

    if (metadata.uploadStatus === UploadStatus.UPLOADED) {
      return 'File uploaded';
    }

    if (metadata.uploadStatus === UploadStatus.VALIDATION_SUCCESS) {
      return 'File validation completed';
    }

    if (metadata.uploadStatus === UploadStatus.COMPLETED && !metadata.exportStatus) {
      return 'Data hashed and encrypted';
    }

    if (!metadata.uploadStatus || !metadata.exportStatus) {
      return 'Status not available';
    }

    if (metadata.uploadStatus === UploadStatus.COMPLETED && metadata.exportStatus === ExportStatus.COMPLETED) {
      return 'Data processed and published';
    }

    return 'Status not available';
  }

  private getSearchText(upload: FileMetadata): string {
    return [
      upload.fileId,
      (upload.recordCount ?? 0).toString(),
      upload.uploadedAt,
      this.getUploadedBy(upload),
      this.getUploadStatusLabel(upload.uploadStatus),
      this.getExportStatusLabel(upload.exportStatus),
      this.getStatusMessage(upload),
      (upload.operatorsProcessed ?? []).join(', '),
      this.getOrgNameValue(upload.orgId) ?? upload.orgId,
    ]
      .join(' ')
      .toLowerCase();
  }

  getStatusTagClass(status?: UploadStatus | ExportStatus | null): string {
    switch (status) {
      case UploadStatus.FAILED:
      case UploadStatus.VALIDATION_FAILED:
      case ExportStatus.FAILED:
        return 'status-tag--failed';
      case UploadStatus.PROCESSING:
      case ExportStatus.IN_PROGRESS:
        return 'status-tag--processing';
      case UploadStatus.UPLOADED:
        return 'status-tag--uploaded';
      case UploadStatus.VALIDATION_SUCCESS:
        return 'status-tag--validation-success';
      case UploadStatus.COMPLETED:
      case ExportStatus.COMPLETED:
        return 'status-tag--completed';
      default:
        return 'status-tag--neutral';
    }
  }

  getStatusIconType(status?: UploadStatus | ExportStatus | null): string {
    switch (status) {
      case UploadStatus.FAILED:
      case UploadStatus.VALIDATION_FAILED:
      case ExportStatus.FAILED:
        return 'close-circle';
      case UploadStatus.PROCESSING:
      case ExportStatus.IN_PROGRESS:
        return 'sync';
      case undefined:
      case null:
        return 'minus-circle';
      default:
        return 'check-circle';
    }
  }

  getStatusMessageClass(upload: FileMetadata): string {
    if (
      upload.uploadStatus === UploadStatus.FAILED ||
      upload.uploadStatus === UploadStatus.VALIDATION_FAILED ||
      upload.exportStatus === ExportStatus.FAILED
    ) {
      return 'status-message--failed';
    }

    if (upload.uploadStatus === UploadStatus.PROCESSING || upload.exportStatus === ExportStatus.IN_PROGRESS) {
      return 'status-message--processing';
    }

    if (upload.uploadStatus === UploadStatus.UPLOADED) {
      return 'status-message--uploaded';
    }

    if (upload.uploadStatus === UploadStatus.VALIDATION_SUCCESS) {
      return 'status-message--validation-success';
    }

    if (!upload.uploadStatus || !upload.exportStatus) {
      return 'status-message--neutral';
    }

    return 'status-message--completed';
  }

  getUploadedAtValue(uploadedAt?: string | null): number | null {
    const uploadDate = uploadedAt ? new Date(uploadedAt) : null;

    return uploadDate && !Number.isNaN(uploadDate.getTime()) ? uploadDate.getTime() : null;
  }

  getUploadedBy(upload: FileMetadata): string {
    return upload.uploadBy ?? '';
  }

  getUploadStatusLabel(uploadStatus?: UploadStatus | null): string {
    return uploadStatus ? uploadStatusLabels[uploadStatus] : 'N/A';
  }

  getExportStatusLabel(exportStatus?: ExportStatus | null): string {
    return exportStatus ? exportStatusLabels[exportStatus] : 'N/A';
  }

  private matchesSelectedStatus(uploadStatus?: UploadStatus | null, exportStatus?: ExportStatus | null): boolean {
    if (this.selectedStatus === UploadStatus.FAILED) {
      return (
        uploadStatus === UploadStatus.FAILED ||
        uploadStatus === UploadStatus.VALIDATION_FAILED ||
        exportStatus === ExportStatus.FAILED
      );
    }

    if (this.selectedStatus === UploadStatus.COMPLETED) {
      return uploadStatus === UploadStatus.COMPLETED || exportStatus === ExportStatus.COMPLETED;
    }

    return uploadStatus === this.selectedStatus;
  }

  getOrgNameValue(orgId: string) {
    return orgMap[orgId];
  }

  getOrgFilterList() {
    this.filterList = this.uploadHistory
      .map((record: FileMetadata) => this.getOrgNameValue(record.orgId!) || record.orgId)
      .filter((orgName, index, arr) => arr && orgName && arr.indexOf(orgName) === index)
      .map((orgName) => {
        return { text: orgName, value: orgName };
      });
  }

  showModal(fileMetadata: FileMetadata): void {
    if (!fileMetadata?.validationResults) {
      this.nzMessageService.error('No Validations logs present for this record');
      return;
    }
    const parseValidationLogs = JSON.parse(fileMetadata?.validationResults || '');

    const currentScroll = document.documentElement.scrollTop;

    this.modalService.create({
      nzTitle: 'Validation Logs',
      nzContent: ValidationLogsComponent,
      nzData: { logs: parseValidationLogs },
      nzOkText: null,
      nzCancelText: null,
      nzWidth: '70rem',
      nzFooter: null,
    });

    setTimeout(() => {
      document.documentElement.style.top = '0px';
      window.scrollTo(0, currentScroll);
    });
  }
}
